/**
 * Hoops application routing configuration
 * Defines all top-level routes with prop extraction and component mapping
 */

import React from 'react';
import { JcAppRoute, JcAppRoutingConfig, JcAppRoutingUtils, JcEmptyProps, JcUtils } from 'jcsdk';
import { HoopsIdUtils } from 'src/services/HoopsIdUtils';
import { HoopsGameSide } from 'src/services/dashboard/models/submodel/HoopsGameSide';
import { HoopsCaptainInterest } from 'src/services/dashboard/models/submodel/HoopsCaptainInterest';

const HoopsNotFoundPage = React.lazy(() => import('../HoopsNotFoundPage').then(m => ({ default: m.HoopsNotFoundPage })));
const HoopsHomePage = React.lazy(() => import('../home/HoopsHomePage').then(m => ({ default: m.HoopsHomePage })));
const HoopsSeasonPage = React.lazy(() => import('../season/HoopsSeasonPage').then(m => ({ default: m.HoopsSeasonPage })));
const HoopsDraftPage = React.lazy(() => import('../season/draft/HoopsDraftPage').then(m => ({ default: m.HoopsDraftPage })));
const HoopsSeasonParticipantsPage = React.lazy(() => import('../season/participants/HoopsSeasonParticipantsPage').then(m => ({ default: m.HoopsSeasonParticipantsPage })));
const HoopsTeamPage = React.lazy(() => import('../season/team/HoopsTeamPage').then(m => ({ default: m.HoopsTeamPage })));
const HoopsTeamEditPage = React.lazy(() => import('../season/team/HoopsTeamEditPage').then(m => ({ default: m.HoopsTeamEditPage })));
const HoopsSchedulePage = React.lazy(() => import('../season/schedule/HoopsSchedulePage').then(m => ({ default: m.HoopsSchedulePage })));
const HoopsWeekPage = React.lazy(() => import('../season/schedule/HoopsWeekPage').then(m => ({ default: m.HoopsWeekPage })));
const HoopsRegistrationPage = React.lazy(() => import('../registration/HoopsRegistrationPage').then(m => ({ default: m.HoopsRegistrationPage })));
const HoopsRegistrationSuccessPage = React.lazy(() => import('../registration/HoopsRegistrationSuccessPage').then(m => ({ default: m.HoopsRegistrationSuccessPage })));
const HoopsSignInPage = React.lazy(() => import('../auth/HoopsSignInPage').then(m => ({ default: m.HoopsSignInPage })));
const HoopsVerifyEmailPage = React.lazy(() => import('../auth/HoopsVerifyEmailPage').then(m => ({ default: m.HoopsVerifyEmailPage })));
const HoopsAccountPage = React.lazy(() => import('../user/HoopsAccountPage').then(m => ({ default: m.HoopsAccountPage })));
const HoopsAccountEditPage = React.lazy(() => import('../user/HoopsAccountEditPage').then(m => ({ default: m.HoopsAccountEditPage })));
const HoopsProfileCreationPage = React.lazy(() => import('../user/HoopsProfileCreationPage').then(m => ({ default: m.HoopsProfileCreationPage })));
const HoopsPlayerPage = React.lazy(() => import('../season/player/HoopsPlayerPage').then(m => ({ default: m.HoopsPlayerPage })));
const HoopsPlayerRegistrationPage = React.lazy(() => import('../season/player/HoopsPlayerRegistrationPage').then(m => ({ default: m.HoopsPlayerRegistrationPage })));
const HoopsPlayerStatsPage = React.lazy(() => import('../season/player/HoopsPlayerStatsPage').then(m => ({ default: m.HoopsPlayerStatsPage })));
const HoopsAdminPage = React.lazy(() => import('../admin/HoopsAdminPage').then(m => ({ default: m.HoopsAdminPage })));
const HoopsAdminCreateSeasonPage = React.lazy(() => import('../admin/season/HoopsAdminCreateSeasonPage').then(m => ({ default: m.HoopsAdminCreateSeasonPage })));
const HoopsAdminSeasonPage = React.lazy(() => import('../admin/season/HoopsAdminSeasonPage').then(m => ({ default: m.HoopsAdminSeasonPage })));
const HoopsSeasonEditPage = React.lazy(() => import('../season/edit/HoopsSeasonEditPage').then(m => ({ default: m.HoopsSeasonEditPage })));
const HoopsGamePage = React.lazy(() => import('../season/game/HoopsGamePage').then(m => ({ default: m.HoopsGamePage })));
const HoopsGameEditPage = React.lazy(() => import('../season/game/HoopsGameEditPage').then(m => ({ default: m.HoopsGameEditPage })));
const HoopsWeekEditPage = React.lazy(() => import('../season/week/HoopsWeekEditPage').then(m => ({ default: m.HoopsWeekEditPage })));
const HoopsSeasonInfoPage = React.lazy(() => import('../season/info/HoopsSeasonInfoPage').then(m => ({ default: m.HoopsSeasonInfoPage })));
const HoopsSeasonStatsPage = React.lazy(() => import('../season/stats/HoopsSeasonStatsPage').then(m => ({ default: m.HoopsSeasonStatsPage })));
const HoopsSeasonInfoEditPage = React.lazy(() => import('../season/info/HoopsSeasonInfoEditPage').then(m => ({ default: m.HoopsSeasonInfoEditPage })));
const HoopsSeasonAwardsPage = React.lazy(() => import('../season/awards/HoopsSeasonAwardsPage').then(m => ({ default: m.HoopsSeasonAwardsPage })));
const HoopsSeasonAwardsEditPage = React.lazy(() => import('../season/awards/HoopsSeasonAwardsEditPage').then(m => ({ default: m.HoopsSeasonAwardsEditPage })));
const HoopsSeasonPlayoffsEditPage = React.lazy(() => import('../season/bracket/HoopsSeasonPlayoffsEditPage').then(m => ({ default: m.HoopsSeasonPlayoffsEditPage })));

export enum SeasonTab {
    SCHEDULE = "schedule",
    STANDINGS = "standings",
    PLAYOFFS = "playoffs",
    TEAM = "team",
    LEAGUE = "league",
}

export interface HoopsSeasonPageProps {
    readonly seasonId: string;
    readonly tab?: SeasonTab;
}

export enum DraftTab {
    BOARD = "board",
    PLAYERS = "players",
    TEAMS = "teams",
}

export interface HoopsDraftPageProps {
    readonly seasonId: string;
    readonly tab?: DraftTab;
}

export interface HoopsSeasonEditPageProps {
    readonly seasonId: string;
}

export enum HoopsScoringView {
    FEED = "feed",
    BOX = "box",
}

export interface HoopsGamePageProps {
    readonly seasonId: string;
    readonly gameId: string;
    /** The scoring panel's Feed/Box-score toggle — kept in the URL so it survives nav-and-back. */
    readonly view?: HoopsScoringView;
    /** The scoring panel's box-score team toggle — only meaningful in box-score view. */
    readonly boxSide?: HoopsGameSide;
}

export interface HoopsGameEditPageProps {
    readonly seasonId: string;
    readonly gameId: string;
}

export interface HoopsSeasonParticipantsPageProps {
    readonly seasonId: string;
    /** The manager-only captain-interest filter — kept in the URL so it survives refresh and deep links. */
    readonly filter?: HoopsCaptainInterest;
}

export interface HoopsSeasonInfoPageProps {
    readonly seasonId: string;
}

export interface HoopsSeasonInfoEditPageProps {
    readonly seasonId: string;
}

export interface HoopsSeasonAwardsPageProps {
    readonly seasonId: string;
}

export interface HoopsSeasonAwardsEditPageProps {
    readonly seasonId: string;
}

export interface HoopsSeasonPlayoffsEditPageProps {
    readonly seasonId: string;
}

export interface HoopsSeasonStatsPageProps {
    readonly seasonId: string;
    /** Team filter (full composite teamId), or undefined for "All". URL query param, short-formed via HoopsIdUtils. */
    readonly teamId?: string;
}

export interface HoopsTeamPageProps {
    readonly seasonId: string;
    readonly teamId: string;
}

export interface HoopsTeamEditPageProps {
    readonly seasonId: string;
    readonly teamId: string;
}

export interface HoopsSchedulePageProps {
    readonly seasonId: string;
}

export interface HoopsWeekPageProps {
    readonly seasonId: string;
    readonly weekId: string;
}

export interface HoopsWeekEditPageProps {
    readonly seasonId: string;
    readonly weekId: string;
}

export interface HoopsRegistrationPageProps {
    readonly seasonId: string;
}

export interface HoopsRegistrationSuccessPageProps {
    readonly seasonId: string;
}

export interface HoopsSignInPageProps {
    readonly returnTo?: string;
}

export interface HoopsVerifyEmailPageProps {
    readonly returnTo?: string;
}

export interface HoopsProfileCreationPageProps {
    readonly returnTo?: string;
}

export interface HoopsSeasonPlayerPageProps {
    readonly seasonId: string;
    readonly username: string;
}

/** Fast-path hint passed via nav state from links that already hold the uid. */
export interface HoopsSeasonPlayerNavState {
    readonly uid: string;
}

export interface HoopsSeasonPlayerRegistrationPageProps {
    readonly seasonId: string;
    readonly username: string;
}

export interface HoopsSeasonPlayerStatsPageProps {
    readonly seasonId: string;
    readonly username: string;
}

export enum AdminSeasonTab {
    TEAMS = "teams",
    PLAYERS = "players"
}

export interface HoopsAdminSeasonPageProps {
    readonly seasonId: string;
    readonly tab?: AdminSeasonTab;
}

/** Reads the optional `returnTo` query param from a path (absent → undefined). */
const readReturnTo = (path: string): string | undefined =>
    JcAppRoutingUtils.extractQueryParams(path).returnTo || undefined;

/** Appends a `?returnTo=...` query string to a base path when `returnTo` is set. */
const withReturnTo = (basePath: string, returnTo: string | undefined): string =>
    returnTo ? `${basePath}?returnTo=${encodeURIComponent(returnTo)}` : basePath;

const HOME_ROUTE: JcAppRoute<JcEmptyProps> = {
    component: HoopsHomePage,
    template: "/",
    propsFromPath: () => ({}),
    pathFromProps: () => "/"
};

const SEASON_ROUTE: JcAppRoute<HoopsSeasonPageProps> = {
    component: HoopsSeasonPage,
    template: "/season/:seasonId/:tab?",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/:tab');
        const tab = JcUtils.validateEnumValue(SeasonTab, params.tab);
        return { seasonId: params.seasonId || '', tab: tab || undefined };
    },
    pathFromProps: (props: HoopsSeasonPageProps) => {
        const basePath = `/season/${props.seasonId}`;
        return props.tab ? `${basePath}/${props.tab}` : basePath;
    }
};

const DRAFT_ROUTE: JcAppRoute<HoopsDraftPageProps> = {
    component: HoopsDraftPage,
    template: "/season/:seasonId/draft/:tab?",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/draft/:tab');
        const tab = JcUtils.validateEnumValue(DraftTab, params.tab);
        return { seasonId: params.seasonId || '', tab: tab || undefined };
    },
    pathFromProps: (props: HoopsDraftPageProps) => {
        const basePath = `/season/${props.seasonId}/draft`;
        return props.tab ? `${basePath}/${props.tab}` : basePath;
    }
};

const SEASON_EDIT_ROUTE: JcAppRoute<HoopsSeasonEditPageProps> = {
    component: HoopsSeasonEditPage,
    template: "/season/:seasonId/edit",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/edit');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsSeasonEditPageProps) => `/season/${props.seasonId}/edit`
};

const SEASON_GAME_ROUTE: JcAppRoute<HoopsGamePageProps> = {
    component: HoopsGamePage,
    template: "/season/:seasonId/games/:gameId",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/games/:gameId');
        const queryParams = JcAppRoutingUtils.extractQueryParams(path);
        const view = JcUtils.validateEnumValue(HoopsScoringView, queryParams.view);
        const boxSide = queryParams.side === HoopsGameSide.HOME || queryParams.side === HoopsGameSide.AWAY
            ? queryParams.side
            : undefined;
        return { seasonId: params.seasonId || '', gameId: params.gameId || '', view, boxSide };
    },
    pathFromProps: (props: HoopsGamePageProps) => {
        const basePath = `/season/${props.seasonId}/games/${props.gameId}`;
        const query: string[] = [];
        if (props.view) query.push(`view=${props.view}`);
        if (props.boxSide) query.push(`side=${props.boxSide}`);
        return query.length > 0 ? `${basePath}?${query.join('&')}` : basePath;
    }
};

const SEASON_GAME_EDIT_ROUTE: JcAppRoute<HoopsGameEditPageProps> = {
    component: HoopsGameEditPage,
    template: "/season/:seasonId/games/:gameId/edit",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/games/:gameId/edit');
        return { seasonId: params.seasonId || '', gameId: params.gameId || '' };
    },
    pathFromProps: (props: HoopsGameEditPageProps) => `/season/${props.seasonId}/games/${props.gameId}/edit`
};

const SEASON_PARTICIPANTS_ROUTE: JcAppRoute<HoopsSeasonParticipantsPageProps> = {
    component: HoopsSeasonParticipantsPage,
    template: "/season/:seasonId/participants",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/participants');
        const queryParams = JcAppRoutingUtils.extractQueryParams(path);
        const filter = JcUtils.validateEnumValue(HoopsCaptainInterest, queryParams.filter);
        return { seasonId: params.seasonId || '', filter };
    },
    pathFromProps: (props: HoopsSeasonParticipantsPageProps) => {
        const basePath = `/season/${props.seasonId}/participants`;
        return props.filter ? `${basePath}?filter=${props.filter}` : basePath;
    }
};

const SEASON_INFO_ROUTE: JcAppRoute<HoopsSeasonInfoPageProps> = {
    component: HoopsSeasonInfoPage,
    template: "/season/:seasonId/info",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/info');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsSeasonInfoPageProps) => `/season/${props.seasonId}/info`
};

const SEASON_INFO_EDIT_ROUTE: JcAppRoute<HoopsSeasonInfoEditPageProps> = {
    component: HoopsSeasonInfoEditPage,
    template: "/season/:seasonId/info/edit",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/info/edit');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsSeasonInfoEditPageProps) => `/season/${props.seasonId}/info/edit`
};

const SEASON_AWARDS_ROUTE: JcAppRoute<HoopsSeasonAwardsPageProps> = {
    component: HoopsSeasonAwardsPage,
    template: "/season/:seasonId/awards",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/awards');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsSeasonAwardsPageProps) => `/season/${props.seasonId}/awards`
};

const SEASON_AWARDS_EDIT_ROUTE: JcAppRoute<HoopsSeasonAwardsEditPageProps> = {
    component: HoopsSeasonAwardsEditPage,
    template: "/season/:seasonId/awards/edit",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/awards/edit');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsSeasonAwardsEditPageProps) => `/season/${props.seasonId}/awards/edit`
};

const SEASON_PLAYOFFS_EDIT_ROUTE: JcAppRoute<HoopsSeasonPlayoffsEditPageProps> = {
    component: HoopsSeasonPlayoffsEditPage,
    template: "/season/:seasonId/playoffs/edit",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/playoffs/edit');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsSeasonPlayoffsEditPageProps) => `/season/${props.seasonId}/playoffs/edit`
};

const SEASON_STATS_ROUTE: JcAppRoute<HoopsSeasonStatsPageProps> = {
    component: HoopsSeasonStatsPage,
    template: "/season/:seasonId/stats",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/stats');
        const queryParams = JcAppRoutingUtils.extractQueryParams(path);
        const seasonId = params.seasonId || '';
        const teamId = queryParams.team ? HoopsIdUtils.compositeId(seasonId, queryParams.team) : undefined;
        return { seasonId, teamId };
    },
    pathFromProps: (props: HoopsSeasonStatsPageProps) => {
        const basePath = `/season/${props.seasonId}/stats`;
        return props.teamId ? `${basePath}?team=${HoopsIdUtils.localSegment(props.teamId, props.seasonId)}` : basePath;
    }
};

const SEASON_TEAM_ROUTE: JcAppRoute<HoopsTeamPageProps> = {
    component: HoopsTeamPage,
    template: "/season/:seasonId/teams/:teamSlug",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/teams/:teamSlug');
        const seasonId = params.seasonId || '';
        return { seasonId, teamId: HoopsIdUtils.compositeId(seasonId, params.teamSlug || '') };
    },
    pathFromProps: (props: HoopsTeamPageProps) =>
        `/season/${props.seasonId}/teams/${HoopsIdUtils.localSegment(props.teamId, props.seasonId)}`
};

const SEASON_TEAM_EDIT_ROUTE: JcAppRoute<HoopsTeamEditPageProps> = {
    component: HoopsTeamEditPage,
    template: "/season/:seasonId/teams/:teamSlug/edit",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/teams/:teamSlug/edit');
        const seasonId = params.seasonId || '';
        return { seasonId, teamId: HoopsIdUtils.compositeId(seasonId, params.teamSlug || '') };
    },
    pathFromProps: (props: HoopsTeamEditPageProps) =>
        `/season/${props.seasonId}/teams/${HoopsIdUtils.localSegment(props.teamId, props.seasonId)}/edit`
};

const SEASON_SCHEDULE_ROUTE: JcAppRoute<HoopsSchedulePageProps> = {
    component: HoopsSchedulePage,
    template: "/season/:seasonId/full-schedule",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/full-schedule');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsSchedulePageProps) => `/season/${props.seasonId}/full-schedule`
};

const SEASON_WEEK_ROUTE: JcAppRoute<HoopsWeekPageProps> = {
    component: HoopsWeekPage,
    template: "/season/:seasonId/weeks/:weekSlug",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/weeks/:weekSlug');
        const seasonId = params.seasonId || '';
        return { seasonId, weekId: HoopsIdUtils.compositeId(seasonId, params.weekSlug || '') };
    },
    pathFromProps: (props: HoopsWeekPageProps) =>
        `/season/${props.seasonId}/weeks/${HoopsIdUtils.localSegment(props.weekId, props.seasonId)}`
};

const SEASON_WEEK_EDIT_ROUTE: JcAppRoute<HoopsWeekEditPageProps> = {
    component: HoopsWeekEditPage,
    template: "/season/:seasonId/weeks/:weekSlug/edit",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/weeks/:weekSlug/edit');
        const seasonId = params.seasonId || '';
        return { seasonId, weekId: HoopsIdUtils.compositeId(seasonId, params.weekSlug || '') };
    },
    pathFromProps: (props: HoopsWeekEditPageProps) =>
        `/season/${props.seasonId}/weeks/${HoopsIdUtils.localSegment(props.weekId, props.seasonId)}/edit`
};

const REGISTRATION_ROUTE: JcAppRoute<HoopsRegistrationPageProps> = {
    component: HoopsRegistrationPage,
    template: "/season/:seasonId/register",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/register');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsRegistrationPageProps) => `/season/${props.seasonId}/register`
};

const REGISTRATION_SUCCESS_ROUTE: JcAppRoute<HoopsRegistrationSuccessPageProps> = {
    component: HoopsRegistrationSuccessPage,
    template: "/season/:seasonId/registered",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/registered');
        return { seasonId: params.seasonId || '' };
    },
    pathFromProps: (props: HoopsRegistrationSuccessPageProps) => `/season/${props.seasonId}/registered`
};

const SIGN_IN_ROUTE: JcAppRoute<HoopsSignInPageProps> = {
    component: HoopsSignInPage,
    template: "/signin",
    propsFromPath: (path: string) => ({ returnTo: readReturnTo(path) }),
    pathFromProps: (props: HoopsSignInPageProps) => withReturnTo("/signin", props.returnTo)
};

const VERIFY_EMAIL_ROUTE: JcAppRoute<HoopsVerifyEmailPageProps> = {
    component: HoopsVerifyEmailPage,
    template: "/verify-email",
    propsFromPath: (path: string) => ({ returnTo: readReturnTo(path) }),
    pathFromProps: (props: HoopsVerifyEmailPageProps) => withReturnTo("/verify-email", props.returnTo)
};

const PROFILE_CREATE_ROUTE: JcAppRoute<HoopsProfileCreationPageProps> = {
    component: HoopsProfileCreationPage,
    template: "/profile/create",
    propsFromPath: (path: string) => ({ returnTo: readReturnTo(path) }),
    pathFromProps: (props: HoopsProfileCreationPageProps) => withReturnTo("/profile/create", props.returnTo)
};

const ACCOUNT_ROUTE: JcAppRoute<JcEmptyProps> = {
    component: HoopsAccountPage,
    template: "/account",
    propsFromPath: () => ({}),
    pathFromProps: () => "/account"
};

const ACCOUNT_EDIT_ROUTE: JcAppRoute<JcEmptyProps> = {
    component: HoopsAccountEditPage,
    template: "/account/edit",
    propsFromPath: () => ({}),
    pathFromProps: () => "/account/edit"
};

const SEASON_PLAYER_ROUTE: JcAppRoute<HoopsSeasonPlayerPageProps, HoopsSeasonPlayerNavState> = {
    component: HoopsPlayerPage,
    template: "/season/:seasonId/players/:username",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/players/:username');
        return { seasonId: params.seasonId || '', username: params.username || '' };
    },
    pathFromProps: (props: HoopsSeasonPlayerPageProps) => `/season/${props.seasonId}/players/${props.username}`
};

const SEASON_PLAYER_REGISTRATION_ROUTE: JcAppRoute<HoopsSeasonPlayerRegistrationPageProps, HoopsSeasonPlayerNavState> = {
    component: HoopsPlayerRegistrationPage,
    template: "/season/:seasonId/players/:username/registration",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/players/:username/registration');
        return { seasonId: params.seasonId || '', username: params.username || '' };
    },
    pathFromProps: (props: HoopsSeasonPlayerRegistrationPageProps) => `/season/${props.seasonId}/players/${props.username}/registration`
};

const SEASON_PLAYER_STATS_ROUTE: JcAppRoute<HoopsSeasonPlayerStatsPageProps, HoopsSeasonPlayerNavState> = {
    component: HoopsPlayerStatsPage,
    template: "/season/:seasonId/players/:username/stats",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/season/:seasonId/players/:username/stats');
        return { seasonId: params.seasonId || '', username: params.username || '' };
    },
    pathFromProps: (props: HoopsSeasonPlayerStatsPageProps) => `/season/${props.seasonId}/players/${props.username}/stats`
};

const ADMIN_ROUTE: JcAppRoute<JcEmptyProps> = {
    component: HoopsAdminPage,
    template: "/admin",
    propsFromPath: () => ({}),
    pathFromProps: () => "/admin"
};

const ADMIN_CREATE_SEASON_ROUTE: JcAppRoute<JcEmptyProps> = {
    component: HoopsAdminCreateSeasonPage,
    template: "/admin/create-season",
    propsFromPath: () => ({}),
    pathFromProps: () => "/admin/create-season"
};

const ADMIN_SEASON_ROUTE: JcAppRoute<HoopsAdminSeasonPageProps> = {
    component: HoopsAdminSeasonPage,
    template: "/admin/season/:seasonId/:tab?",
    propsFromPath: (path: string) => {
        const params = JcAppRoutingUtils.extractUrlParams(path, '/admin/season/:seasonId/:tab');
        const tab = JcUtils.validateEnumValue(AdminSeasonTab, params.tab);
        return { seasonId: params.seasonId || '', tab: tab || undefined };
    },
    pathFromProps: (props: HoopsAdminSeasonPageProps) => {
        const basePath = `/admin/season/${props.seasonId}`;
        return props.tab ? `${basePath}/${props.tab}` : basePath;
    }
};

export const hoopsRoutes = {
    HOME: HOME_ROUTE,
    SEASON: SEASON_ROUTE,
    DRAFT: DRAFT_ROUTE,
    SEASON_EDIT: SEASON_EDIT_ROUTE,
    SEASON_GAME: SEASON_GAME_ROUTE,
    SEASON_GAME_EDIT: SEASON_GAME_EDIT_ROUTE,
    SEASON_PARTICIPANTS: SEASON_PARTICIPANTS_ROUTE,
    SEASON_INFO: SEASON_INFO_ROUTE,
    SEASON_INFO_EDIT: SEASON_INFO_EDIT_ROUTE,
    SEASON_AWARDS: SEASON_AWARDS_ROUTE,
    SEASON_AWARDS_EDIT: SEASON_AWARDS_EDIT_ROUTE,
    SEASON_PLAYOFFS_EDIT: SEASON_PLAYOFFS_EDIT_ROUTE,
    SEASON_STATS: SEASON_STATS_ROUTE,
    SEASON_TEAM: SEASON_TEAM_ROUTE,
    SEASON_TEAM_EDIT: SEASON_TEAM_EDIT_ROUTE,
    SEASON_SCHEDULE: SEASON_SCHEDULE_ROUTE,
    SEASON_WEEK: SEASON_WEEK_ROUTE,
    SEASON_WEEK_EDIT: SEASON_WEEK_EDIT_ROUTE,
    REGISTRATION: REGISTRATION_ROUTE,
    REGISTRATION_SUCCESS: REGISTRATION_SUCCESS_ROUTE,
    SIGN_IN: SIGN_IN_ROUTE,
    VERIFY_EMAIL: VERIFY_EMAIL_ROUTE,
    PROFILE_CREATE: PROFILE_CREATE_ROUTE,
    ACCOUNT: ACCOUNT_ROUTE,
    ACCOUNT_EDIT: ACCOUNT_EDIT_ROUTE,
    SEASON_PLAYER: SEASON_PLAYER_ROUTE,
    SEASON_PLAYER_REGISTRATION: SEASON_PLAYER_REGISTRATION_ROUTE,
    SEASON_PLAYER_STATS: SEASON_PLAYER_STATS_ROUTE,
    ADMIN: ADMIN_ROUTE,
    ADMIN_CREATE_SEASON: ADMIN_CREATE_SEASON_ROUTE,
    ADMIN_SEASON: ADMIN_SEASON_ROUTE
} as const;

export type HoopsRoutes = typeof hoopsRoutes;

export const hoopsAppRoutingConfig: JcAppRoutingConfig<HoopsRoutes> = {
    defaultRoute: "HOME",
    notFoundPage: HoopsNotFoundPage,
    routes: hoopsRoutes
};

export type HoopsRouteKey = keyof HoopsRoutes;
